import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart, Line,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor, type PeriodContentProps } from './shared';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';

const PALETTE = {
  cumSpend: '#0f172a',     // slate-900 — cumulative line stays dark
  monthSpend: '#3b82f6',   // blue-500 — punchier month bars
  arrears: '#dc2626',
  target: '#94a3b8',
  ncr: '#dc2626',          // align with quality-red discipline
};

const TARGET_PCT = 95;

export function YTD({ data, formatCurrency }: PeriodContentProps) {
  const ytd = data.ytd;
  const qualityLabel = useQualityMeasureLabel();

  // Real per-month YTD data from the backend — sums to exactly the YTD
  // top-tile total. ``otif`` / ``otd`` are null when no GRN had an
  // attributed status that month, so the line breaks instead of dropping
  // to a misleading 0%.
  let cum = 0;
  const monthly = data.per_month.months.map((m) => {
    cum += m.cost.received_value;
    const hasDeliveryData = m.delivery.total_due > 0;
    return {
      month: m.label,
      received: m.cost.received_value,
      open:     m.cost.open_value,
      committed: m.cost.total_value,
      cumReceived: cum,
      ncrs: m.quality.ncrs,
      otif: hasDeliveryData ? m.delivery.otif_pct : null,
      otd:  hasDeliveryData ? m.delivery.otd_pct  : null,
      target: TARGET_PCT,
    };
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red" subtitle="Jan 1 → today">
          <div className="grid grid-cols-2 gap-3">
            <KPICell label={qualityLabel} value={<span className="text-2xl font-semibold tabular-nums">{ytd.quality.ppm.toLocaleString()}</span>} foot={`${ytd.quality.reject_qty.toLocaleString()} rej`} />
            <KPICell label="NCRs" value={<span className="text-2xl font-semibold tabular-nums">{ytd.quality.ncrs.toLocaleString()}</span>} foot="cumulative" />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle="Jan 1 → today">
          <div className="grid grid-cols-3 gap-3 items-start">
            <KPICell label="OTIF" value={<CircularProgress percentage={ytd.delivery.otif_pct} size={68} strokeWidth={6} color={performanceColor(ytd.delivery.otif_pct)} />} foot={`${ytd.delivery.otif_met.toLocaleString()}/${ytd.delivery.total_due.toLocaleString()} schedules`} />
            <KPICell label="OTD" value={<CircularProgress percentage={ytd.delivery.otd_pct} size={68} strokeWidth={6} color={performanceColor(ytd.delivery.otd_pct)} />} foot={`${ytd.delivery.otd_met.toLocaleString()}/${ytd.delivery.total_due.toLocaleString()} within firm`} />
            <KPICell label="Appeals" value={<span className="text-2xl font-semibold tabular-nums">{ytd.delivery.appeals}</span>} />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="schedules due YTD">
          <div className="grid grid-cols-3 gap-3">
            <KPICell label="Open Schedule Lines" value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.open_value)}</span>} foot={`${ytd.cost.open_lines.toLocaleString()} of ${ytd.cost.total_lines.toLocaleString()} lines`} />
            <KPICell label="Received (YTD)" value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.received_value)}</span>} foot={`of ${formatCurrency(ytd.cost.total_value)} committed`} />
            <KPICell label="Arrears" value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.arrears_value)}</span>} foot={`${ytd.cost.arrears_lines.toLocaleString()} past-due lines`} />
          </div>
        </SectionFrame>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">Schedule Value by Month — Received vs Open</CardTitle>
          <p className="text-[11px] text-muted-foreground">Bars: schedule value due that month (split received / still open) • Line: YTD cumulative received</p>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000000 ? `£${(v/1000000).toFixed(1)}M` : `£${(v/1000).toFixed(0)}k`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000000 ? `£${(v/1000000).toFixed(0)}M` : `£${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                <Bar yAxisId="left" dataKey="received" stackId="cost" fill={PALETTE.monthSpend} name="Received" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="left" dataKey="open"     stackId="cost" fill="#cbd5e1"             name="Open"     radius={[3, 3, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="cumReceived" stroke={PALETTE.cumSpend} strokeWidth={2.5} dot={{ r: 3 }} name="Cumulative received" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Delivery — OTIF / OTD by Month</CardTitle>
            <p className="text-[11px] text-muted-foreground">Target {monthly[0]?.target ?? 95}% (dashed)</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <ReferenceLine y={monthly[0]?.target ?? 95} stroke={PALETTE.target} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="otif" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} name="OTIF" connectNulls={false} />
                  <Line type="monotone" dataKey="otd"  stroke="#0284c7" strokeWidth={2.5} dot={{ r: 3 }} name="OTD"  connectNulls={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">NCRs — Monthly</CardTitle>
            <p className="text-[11px] text-muted-foreground">Issues raised against customers</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Bar dataKey="ncrs" fill={PALETTE.ncr} name="NCRs" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
